import { tryAgainLabel } from 'owa-locstrings/lib/strings/tryagainlabel.locstring.json';
import handleCalendarServiceError from './handleCalendarServiceError';
import renameCalendarService from '../services/renameCalendarService';
import { Calendar, CalendarId } from 'owa-calendar-cache';
import { processRenameServiceResponse } from '../utils/processRenameServiceResponse';
import { getCalendars, instantRenameCalendar } from 'owa-calendar-surface-calendars-store';
import { ActionDatapoint, returnTopExecutingActionDatapoint } from 'owa-analytics';
import { createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { findInArray } from 'owa-calendar-data-utils/lib/findInArray';
import { lazyShowCalendarNotification } from 'owa-calendar-notification-bar';
import { ControlIcons } from 'owa-control-icons';
import loc, { format } from 'owa-localize';
import * as trace from 'owa-trace';
import {
    calendarNotificationsBarRenameCalendarFailed,
    calendarNotificationsBarRenameCalendar,
} from './renameCalendar.locstring.json';
import showCalendarErrorNotification from './showCalendarErrorNotification';

/**
 * Action to rename calendar group
 */
export let renameCalendar = createLegacyActionWithDatapoint('RenameCalendar', {
    customData: (calendar: Calendar, newCalendarName: string) => {
        return { rejected: calendar.displayName === newCalendarName };
    },
})(async function renameCalendarInternal(
    calendar: Calendar,
    newCalendarName: string
): Promise<void> {
    if (calendar.displayName === newCalendarName) {
        return;
    }
    const dataPoint = returnTopExecutingActionDatapoint();
    const oldCalendarName = calendar.displayName;

    await handleCalendarServiceError(
        constructRequestCallback(calendar, dataPoint, newCalendarName),
        constructRequestFailedCallback(calendar.id, oldCalendarName, newCalendarName, calendar)
    );
});

function constructRequestCallback(
    calendar: Calendar,
    dataPoint: ActionDatapoint,
    newCalendarName: string
) {
    return async () => {
        instantRenameCalendar(calendar.id, newCalendarName);
        if (dataPoint) {
            dataPoint.markUserPerceivedTime(); // mark user perceived time here as soon as we dispatch the request to rename
        }

        lazyShowCalendarNotification.import().then(showCalendarNotification => {
            showCalendarNotification(
                'renameCalendar',
                'Surface',
                format(loc(calendarNotificationsBarRenameCalendar), newCalendarName),
                { icon: ControlIcons.Calendar }
            );
        });

        const response = await renameCalendarService(calendar, newCalendarName);
        processRenameServiceResponse(response, calendar, getCalendars(), newCalendarName);

        return response;
    };
}

function constructRequestFailedCallback(
    id: CalendarId,
    oldCalendarName: string,
    newCalendarName: string,
    calendar: Calendar
) {
    return () => {
        const calendars = getCalendars();
        let foundCalendarResult = findInArray(calendars, item => {
            return item.id.id == id.id;
        });

        if (foundCalendarResult) {
            // mutate the calendar object in the calendars list so that the
            // change gets reflected in selectedCalendars list as well
            calendars[foundCalendarResult.index].displayName = oldCalendarName;
        } else {
            trace.errorThatWillCauseAlert(`[renameCalendar] Cannot find calendar id: ${id}`);
        }

        showCalendarErrorNotification(
            'renameCalendar',
            format(loc(calendarNotificationsBarRenameCalendarFailed), oldCalendarName),
            ControlIcons.Error,
            loc(tryAgainLabel),
            () => renameCalendar(calendar, newCalendarName)
        );
    };
}

export default renameCalendar;
